import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function CareersSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const positions = [
    {
      titleKey: "careers.position1.title",
      descKey: "careers.position1.desc",
    },
    {
      titleKey: "careers.position2.title",
      descKey: "careers.position2.desc",
    },
    {
      titleKey: "careers.position3.title",
      descKey: "careers.position3.desc",
    },
  ];

  return (
    <section
      id="careers"
      ref={sectionRef}
      className="bg-white py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-14 md:mb-20">
            <div className="animate-in opacity-0 translate-y-6 transition-all duration-700">
              <span className="text-xs text-ax-silver-dark tracking-[0.3em] uppercase block mb-4">
                {t("careers.label")}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ax-dark font-bold mb-4">
                {t("careers.title")}
              </h2>
              <p className="text-ax-dark/60 text-sm md:text-base max-w-xl mx-auto">
                {t("careers.description")}
              </p>
            </div>
          </div>

          {/* Open Positions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16">
            {positions.map((pos, i) => (
              <div
                key={t(pos.titleKey)}
                className="animate-in opacity-0 translate-y-6 transition-all duration-700 bg-ax-light/50 border border-ax-silver-light rounded-2xl p-6 md:p-8 hover:border-ax-red/30 hover:shadow-lg transition-all"
                style={{ transitionDelay: `${200 + i * 200}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ax-red/10 mb-5">
                  <i
                    className={`text-ax-red text-lg ${
                      i === 0
                        ? "ri-code-s-slash-line"
                        : i === 1
                          ? "ri-cpu-line"
                          : "ri-global-line"
                    }`}
                  />
                </div>
                <h3 className="font-serif text-lg font-bold text-ax-dark mb-3">
                  {t(pos.titleKey)}
                </h3>
                <p className="text-ax-dark/50 text-sm leading-relaxed">
                  {t(pos.descKey)}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-700 text-center">
            <div className="inline-flex flex-col items-center bg-ax-dark rounded-2xl px-8 md:px-14 py-8 md:py-10">
              <i className="ri-mail-send-line text-3xl text-ax-red mb-4" />
              <p className="text-white font-medium text-base md:text-lg mb-2">
                {t("careers.ctaTitle")}
              </p>
              <p className="text-ax-silver-dark text-sm mb-5">
                {t("careers.ctaDesc")}
              </p>
              <a
                href="mailto:hr@axaxis.com"
                className="inline-flex items-center gap-2 bg-ax-red hover:bg-ax-red-dark text-white text-sm font-medium px-8 py-3 rounded-lg transition-colors"
              >
                hr@axaxis.com
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
